import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { 
  MapPin, Phone, Clock, Shield, Star, CheckCircle, 
  Wrench, Award, Users, ArrowRight, ChevronDown,
  Refrigerator, WashingMachine, CookingPot, Microwave
} from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

// Beyaz eşya kategorileri
const kategoriler = [
  { id: 'buzdolabi', name: 'Buzdolabı', icon: Refrigerator, desc: 'No-frost, çift kapılı, mini buzdolabı' },
  { id: 'camasir', name: 'Çamaşır Makinesi', icon: WashingMachine, desc: 'Kurutmalı, tam otomatik, yarı otomatik' },
  { id: 'bulasik', name: 'Bulaşık Makinesi', icon: CookingPot, desc: 'Ankastre, solo, yarım boy' },
  { id: 'firin', name: 'Fırın & Ocak', icon: CookingPot, desc: 'Ankastre fırın, set üstü ocak, davlumbaz' },
  { id: 'kurutma', name: 'Kurutma Makinesi', icon: WashingMachine, desc: 'Isı pompalı, kondensasyonlu' },
  { id: 'diger', name: 'Diğer', icon: Microwave, desc: 'Mikrodalga, derin dondurucu, şofben' },
];

// Markalar
const markalar = [
  { name: 'Arçelik', color: '#1E40AF' },
  { name: 'Beko', color: '#DC2626' },
  { name: 'Bosch', color: '#E30613' },
  { name: 'Siemens', color: '#009999' },
  { name: 'Samsung', color: '#1428A0' },
  { name: 'LG', color: '#A50034' },
  { name: 'Vestel', color: '#0D47A1' },
  { name: 'Profilo', color: '#1E3A8A' },
  { name: 'Whirlpool', color: '#FFD100' },
  { name: 'Electrolux', color: '#041E42' },
  { name: 'AEG', color: '#C8102E' },
  { name: 'Grundig', color: '#E30613' },
];

// Yaygın arızalar
const yaygınArizalar = {
  buzdolabi: [
    { ariza: 'Soğutmuyor', sebep: 'Termostat, kompresör veya gaz kaçağı', cozum: 'Profesyonel teşhis gerekli' },
    { ariza: 'Buzlanma', sebep: 'No-frost fan arızası veya kapı contası', cozum: 'Fan veya conta değişimi' },
    { ariza: 'Su sızıntısı', sebep: 'Tahliye deliği tıkanıklığı', cozum: 'Tahliye temizliği' },
    { ariza: 'Gürültülü çalışma', sebep: 'Fan veya kompresör sorunu', cozum: 'Parça değişimi gerekebilir' },
  ],
  camasir: [
    { ariza: 'Su almıyor', sebep: 'Vana arızası veya filtre tıkanıklığı', cozum: 'Vana/filtre kontrolü' },
    { ariza: 'Sıkmıyor', sebep: 'Pompa arızası veya kapak kilidi', cozum: 'Pompa/kilit değişimi' },
    { ariza: 'Titreşim', sebep: 'Amortisör veya yay sorunu', cozum: 'Amortisör değişimi' },
    { ariza: 'Kötü koku', sebep: 'Deterjan birikimi, küf', cozum: '90°C boş yıkama + temizlik' },
  ],
  bulasik: [
    { ariza: 'Temizlemiyor', sebep: 'Püskürtme kolu tıkanık', cozum: 'Kol temizliği/değişimi' },
    { ariza: 'Kurutmuyor', sebep: 'Isıtıcı veya fan arızası', cozum: 'Parça değişimi' },
    { ariza: 'Su tahliye etmiyor', sebep: 'Pompa veya filtre sorunu', cozum: 'Pompa/filtre kontrolü' },
    { ariza: 'Kötü koku', sebep: 'Filtre kirliliği', cozum: 'Filtre temizliği + yıkama' },
  ],
  firin: [
    { ariza: 'Isınmıyor', sebep: 'Rezistans veya termostat arızası', cozum: 'Rezistans değişimi' },
    { ariza: 'Ocak yanmıyor', sebep: 'Ateşleme veya gaz valfi sorunu', cozum: 'Valf/buji kontrolü' },
    { ariza: 'Fan çalışmıyor', sebep: 'Fan motoru arızası', cozum: 'Motor değişimi' },
    { ariza: 'Kapak kapanmıyor', sebep: 'Menteşe kırık', cozum: 'Menteşe değişimi' },
  ],
};

// Hizmetler
const hizmetler = [
  { icon: Wrench, title: 'Arıza Onarım', desc: 'Tüm marka ve modellerde uzman tamir', price: '₺500\'den başlayan' },
  { icon: Shield, title: 'Bakım', desc: 'Periyodik bakım ve kontrol', price: '₺300\'den başlayan' },
  { icon: CookingPot, title: 'Parça Değişimi', desc: 'Orijinal yedek parça garantisi', price: 'Parçaya göre' },
  { icon: Award, title: 'Kurulum', desc: 'Profesyonel montaj hizmeti', price: '₺400\'den başlayan' },
];

// SSS
const sikSorulanSorular = [
  {
    soru: 'Beyaz eşya tamiri ne kadar sürer?',
    cevap: 'Basit arızalar aynı gün, parça gerektiren işlemler 1-3 gün içinde tamamlanır. Parça tedariği durumunda süre uzayabilir.'
  },
  {
    soru: 'Garantisi biten cihazı tamir ettirmeli miyim?',
    cevap: 'Cihazınız 8-10 yaşından küçükse tamir ekonomik olabilir. Daha eski cihazlarda yeni ürün almanız avantajlı olabilir.'
  },
  {
    soru: 'Orijinal parça kullanılıyor mu?',
    cevap: 'Evet, tüm tamirlerimizde orijinal veya orijinal muadili kaliteli parçalar kullanılır. Parça garantisi verilir.'
  },
  {
    soru: 'Arıza tespiti ücretli mi?',
    cevap: 'Tamir yaptırılması durumunda arıza tespit ücreti alınmaz. Tamir yapılmazsa minimum servis ücreti alınır.'
  },
  {
    soru: 'Aynı gün servis mümkün mü?',
    cevap: 'Evet, çoğu bölgede aynı gün veya ertesi gün servis imkanı sunuyoruz. Acil durumlar için 7/24 destek hattımız mevcut.'
  },
];

const BeyazEsyaServisi = () => {
  const navigate = useNavigate();
  const [selectedKategori, setSelectedKategori] = useState('buzdolabi');
  const [openFaq, setOpenFaq] = useState(null);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "HizmetGeliyo Beyaz Eşya Servisi",
    "description": "Buzdolabı, çamaşır makinesi, bulaşık makinesi tamiri. Tüm marka ve modellerde uzman servis. Orijinal parça garantisi.",
    "url": "https://hizmetgeliyo.com/beyaz-esya-servisi",
    "telephone": "+90-850-XXX-XXXX",
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "TR"
    },
    "openingHours": "Mo-Su 08:00-22:00",
    "priceRange": "₺₺",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.7",
      "reviewCount": "890"
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-purple-50/30">
      <Helmet>
        <title>Beyaz Eşya Servisi - Buzdolabı, Çamaşır, Bulaşık Makinesi Tamiri | HizmetGeliyo</title>
        <meta name="description" content="Beyaz eşya tamiri: buzdolabı, çamaşır makinesi, bulaşık makinesi, fırın. Arçelik, Bosch, Samsung tüm markalar. Orijinal parça, aynı gün servis, garanti." />
        <meta name="keywords" content="beyaz eşya servisi, buzdolabı tamiri, çamaşır makinesi tamiri, bulaşık makinesi tamiri, fırın tamiri, arçelik servisi, bosch servisi, samsung servisi" />
        <link rel="canonical" href="https://hizmetgeliyo.com/beyaz-esya-servisi" />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>

      <Navbar />

      {/* Hero */}
      <section className="relative pt-20 pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-600 via-violet-600 to-indigo-600"></div>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-purple-300 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <Refrigerator className="w-5 h-5 text-white" />
              <span className="text-white font-medium">Profesyonel Beyaz Eşya Servisi</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              <span className="text-purple-200">Beyaz Eşya</span> Tamiri
            </h1>
            
            <p className="text-xl text-purple-100 max-w-3xl mx-auto mb-8">
              Buzdolabı, çamaşır makinesi, bulaşık makinesi, fırın tamiri. Tüm markalar.
              <strong className="text-white"> Orijinal parça</strong>, aynı gün servis.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Button 
                onClick={() => navigate('/talep/beyaz-esya')}
                className="bg-white text-purple-600 hover:bg-purple-50 px-8 py-4 rounded-full text-lg font-semibold shadow-xl"
              >
                <Wrench className="w-5 h-5 mr-2" />
                Ücretsiz Teklif Al
              </Button>
              <Button 
                variant="outline"
                className="border-2 border-white text-white hover:bg-white/10 px-8 py-4 rounded-full text-lg"
              >
                <Phone className="w-5 h-5 mr-2" />
                Hemen Ara
              </Button>
            </div>

            {/* Trust Badges */}
            <div className="flex flex-wrap justify-center gap-6 text-white/90">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-purple-300" />
                <span>Orijinal Parça</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-purple-300" />
                <span>1 Yıl Garanti</span>
              </div>
              <div className="flex items-center gap-2">
                <Star className="w-5 h-5 text-purple-300" />
                <span>4.7/5 Puan</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-purple-300" />
                <span>Aynı Gün Servis</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Kategoriler */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Hangi Cihazınızda Sorun Var?</h2>
            <p className="text-gray-600">Cihaz türünü seçin, sorununuzu çözelim</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {kategoriler.map((kat) => (
              <button
                key={kat.id}
                onClick={() => { setSelectedKategori(kat.id); }}
                className={`p-6 rounded-2xl border-2 transition-all text-center ${
                  selectedKategori === kat.id
                    ? 'border-purple-500 bg-purple-50'
                    : 'border-gray-200 hover:border-purple-300 bg-white'
                }`}
              >
                <kat.icon className={`w-10 h-10 mx-auto mb-3 ${selectedKategori === kat.id ? 'text-purple-600' : 'text-gray-500'}`} />
                <p className="font-semibold text-gray-900">{kat.name}</p>
                <p className="text-xs text-gray-500 mt-1">{kat.desc}</p>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Yaygın Arızalar */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {kategoriler.find(k => k.id === selectedKategori)?.name} Arızaları
            </h2>
            <p className="text-gray-600">En sık karşılaşılan sorunlar ve çözümleri</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {yaygınArizalar[selectedKategori]?.map((item, idx) => (
              <div key={idx} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h3 className="font-bold text-lg text-gray-900 mb-2">{item.ariza}</h3>
                <p className="text-gray-600 text-sm mb-3"><strong>Olası Sebep:</strong> {item.sebep}</p>
                <p className="text-purple-600 text-sm"><strong>Çözüm:</strong> {item.cozum}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-8">
            <Button 
              onClick={() => navigate('/talep/beyaz-esya')}
              className="bg-purple-600 hover:bg-purple-700"
            >
              Servis Talebi Oluştur
            </Button>
          </div>
        </div>
      </section>

      {/* Hizmetler */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Hizmetlerimiz</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {hizmetler.map((hizmet, idx) => (
              <div 
                key={idx}
                className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-2xl p-6 hover:shadow-xl transition-all border border-purple-100"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-violet-500 rounded-xl flex items-center justify-center mb-4">
                  <hizmet.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{hizmet.title}</h3>
                <p className="text-gray-600 text-sm mb-3">{hizmet.desc}</p>
                <p className="text-purple-600 font-semibold">{hizmet.price}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Markalar */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Tüm Markalara Hizmet</h2>
          </div>

          <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {markalar.map((marka, idx) => (
              <div 
                key={idx}
                className="bg-white rounded-xl p-4 flex items-center justify-center h-16 shadow-sm hover:shadow-md transition-shadow"
              >
                <span className="font-bold text-gray-700">{marka.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SSS */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Sıkça Sorulan Sorular</h2>
          </div>

          <div className="space-y-4">
            {sikSorulanSorular.map((faq, idx) => (
              <div key={idx} className="border border-gray-200 rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  className="w-full px-6 py-4 flex items-center justify-between bg-white hover:bg-gray-50"
                >
                  <span className="font-semibold text-gray-900 text-left">{faq.soru}</span>
                  <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform ${openFaq === idx ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === idx && (
                  <div className="px-6 py-4 bg-gray-50 border-t">
                    <p className="text-gray-600">{faq.cevap}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-purple-600 to-violet-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Beyaz Eşyanız Arızalandı mı?
          </h2>
          <p className="text-purple-100 text-lg mb-8">
            Orijinal parça ve uzman ekiple cihazınızı hızlıca onarıyoruz.
          </p>
          <Button 
            onClick={() => navigate('/talep/beyaz-esya')}
            className="bg-white text-purple-600 hover:bg-purple-50 px-8 py-4 rounded-full text-lg font-semibold"
          >
            Ücretsiz Teklif Al <ArrowRight className="w-5 h-5 ml-2 inline" />
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BeyazEsyaServisi;
